// SidebarContent.jsx - FINAL VERSION
import React, { Fragment, useState, useEffect } from "react";
import {
  Box,
  Flex,
  Icon,
  Text,
  CloseButton,
  Image,
  Collapse,
  useToast,
} from "@chakra-ui/react";
import { FiHome } from "react-icons/fi";
import { FaMagic, FaAngleDown } from "react-icons/fa";
import { TiFlowSwitch } from "react-icons/ti";
import { IoIosPeople } from "react-icons/io";
import { MdOutlineCampaign } from "react-icons/md";
import { HiOutlineTemplate } from "react-icons/hi";
import { MdGroupWork } from "react-icons/md";
import { RiAdvertisementLine } from "react-icons/ri";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import { fetchPowerBiUrl, getEnabledMenuItems } from "../Services/api";
import { getTokenFromLoacalStorage } from "../_helpers/helpers";
import SelectedOrganization from "../components/SelectedOrganization";

const LinkItems = [
  { name: "Dashboard", icon: FiHome, to: "/" },
  { name: "Flows", icon: TiFlowSwitch, to: "/flows" },
  {
    name: "Control Group",
    icon: MdGroupWork,
    to: "/",
    children: [
      { name: "Dashboard", to: "/control-group" },
      { name: "Customer List", to: "/control-group-list" },
    ],
  },
  {
    name: "Audience",
    icon: IoIosPeople,
    to: "/",
    children: [
      { name: "Segment", to: "/audience/segment" },

      { name: "Anonymous Subscribers", to: "/audience/anonymous-subscribers" },

      { name: "Live Activities", to: "/audience/live-activities" },
      { name: "Suppression List", to: "/audience/suppression-list" },
      { name: "Customers", to: "/audience/customers-segment" },
      { name: "Orders", to: "/audience/orders" },
      { name: "Products", to: "/audience/products/overview" },
    ],
  },
  { name: "Campaign", icon: MdOutlineCampaign, to: "/campaign" },
  { name: "Templates", icon: HiOutlineTemplate, to: "/templates" },
  { name: "All Banks", icon: MdOutlineCampaign, to: "/all-banks/list" },
  { name: "Exit Intent", icon: MdOutlineCampaign, to: "/exit-intent-list" },
  { name: "Opt In Link", icon: MdOutlineCampaign, to: "/optin-link" },
  {
    name: "Conversations",
    icon: MdOutlineCampaign,
    to: "#",
    onClick: () => {
      const authData = getTokenFromLoacalStorage();
      if (!authData?.jwt || !authData?.selectedOrg) return;
      let orgToken;
      try {
        orgToken = JSON.parse(authData.selectedOrg)?.token;
      } catch (e) { return; }
      const url = `${process.env.REACT_APP_OMNI_CHAT_ENDPOINT}/auth/?token=${authData.jwt}&org=${orgToken}&isSuperAdmin=${authData.isSuperAdmin || false}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
  },
  {
    name: "Ads",
    icon: RiAdvertisementLine,
    to: "#",
    onClick: () => {
      const authData = getTokenFromLoacalStorage();
      if (!authData?.jwt || !authData?.selectedOrg) return;
      let orgToken;
      try {
        orgToken = JSON.parse(authData.selectedOrg)?.token;
      } catch (e) { return; }
      const url = `${process.env.REACT_APP_RAY_TARGET_URL}/auth/?token=${authData.jwt}&org=${orgToken}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
  },
  {
    name: "Content Personalization",
    icon: FaMagic,
    to: "/",
    children: [
      { name: "Banner Personalization", to: "/content-personalization" },
      { name: "Smart Block", to: "/smart-block" },
    ],
  },
];

const NavItem = ({ itemName, icon: IconComponent, to, onClick, childrens }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const parentActive = to === "/" 
    ? location.pathname === "/" 
    : location.pathname.startsWith(to);

  const childActive = childrens?.some(child => location.pathname.startsWith(child.to));
  const isActive = parentActive || childActive;

  useEffect(() => {
    if (isActive && childrens) setIsOpen(true);
  }, [isActive, childrens]);

  const hasChildren = childrens && childrens.length > 0;

  const handleClick = (e) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    } else if (hasChildren) {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  return (
    <>
      <Box
        as={onClick || hasChildren ? "div" : NavLink}
        to={onClick || hasChildren ? undefined : to}
        onClick={handleClick}
        cursor="pointer"
        mb="1"
      >
        <Flex
          align="center"
          px="4"
          py="3"
          borderRadius="12px"
          mx="3"
          fontWeight="500"
          fontSize="14px"
          color="white"
          bg={isActive ? "rgba(167, 139, 255, 0.25)" : "transparent"}
          border={isActive ? "1px solid rgba(167, 139, 255, 0.5)" : "1px solid transparent"}
          backdropFilter={isActive ? "blur(12px)" : "none"}
          boxShadow={isActive ? "0 4px 20px rgba(167, 139, 255, 0.2)" : "none"}
          transition="all 0.25s ease"
          _hover={{
            bg: isActive ? "rgba(167, 139, 255, 0.35)" : "rgba(255, 255, 255, 0.08)",
            transform: "translateX(6px)",
            boxShadow: "0 6px 25px rgba(167, 139, 255, 0.25)",
          }}
        >
          {IconComponent && (
            <Icon as={IconComponent} mr="3" fontSize="20" color={isActive ? "#c4b5fd" : "#e0e7ff"} />
          )}
          <Text color={'#fff'} flex="1">{itemName}</Text>
          {hasChildren && (
            <Icon
              as={FaAngleDown}
              fontSize="16"
              transition="transform 0.3s"
              transform={isOpen ? "rotate(180deg)" : "rotate(0)"}
              color="#e0e7ff"
            />
          )}
        </Flex>
      </Box>

      {hasChildren && (
        <Collapse in={isOpen} animateOpacity>
          <Box pl="10" pr="4" mt="2" mb="2">
            {childrens.map((child) => {
              const isChildActive = location.pathname.startsWith(child.to);
              return (
                <NavLink key={child.name} to={child.to}>
                  <Flex
                    align="center"
                    py="2.5"
                    pl="4"
                    borderRadius="10px"
                    fontSize="13.5px"
                    fontWeight="500"
                    color="white"
                    bg={isChildActive ? "rgba(167, 139, 255, 0.25)" : "transparent"}
                    border={isChildActive ? "1px solid rgba(167, 139, 255, 0.5)" : "1px solid transparent"}
                    backdropFilter={isChildActive ? "blur(12px)" : "none"}
                    boxShadow={isChildActive ? "0 4px 20px rgba(167, 139, 255, 0.2)" : "none"}
                    transition="all 0.25s ease"
                    _hover={{
                      bg: "rgba(167, 139, 255, 0.25)",
                      border: "1px solid rgba(167, 139, 255, 0.5)",
                      pl: "5",
                    }}
                  >
                    <Text color={'#fff'} >{child.name}</Text>
                  </Flex>
                </NavLink>
              );
            })}
          </Box>
        </Collapse>
      )}
    </>
  );
};

export default function SidebarContent({ onClose }) {
  const toast = useToast();
  const { white_label } = useSelector((state) => state.auth) || {};
  const mainLogo = white_label?.data?.lightBgLogo || white_label?.lightBgLogo 

  const [enabledMenuItems, setEnabledMenuItems] = useState([]);

  const userData = getTokenFromLoacalStorage()?.user_data?.extraData;
  const isAllBanks = userData?.isAllBanks;
  const isAllFeaturesUser = userData?.all_features_user;

  useEffect(() => {
    getEnabledMenuItems()
      .then(res => setEnabledMenuItems(res.data || []))
      .catch(() => toast({ title: "Failed to load menu permissions", status: "error" }));
  }, [toast]);

  // Power BI integration (kept but simplified)
  useQuery("powerBi", () => fetchPowerBiUrl(), {
    enabled: false,
    onSuccess: (res) => {
      try {
        const data = JSON.parse(res.data.powerbiUrl || "[]");
        if (Array.isArray(data) && data.length > 0) {
          const items = data.map(d => ({
            name: d.label,
            to: `/dashboard/${d.label}/${btoa(d.url).replaceAll("/", "mkt")}`
          }));
          const dashIndex = LinkItems.findIndex(i => i.name === "Dashboard");
          if (dashIndex !== -1) LinkItems[dashIndex].children = items;
        }
      } catch (e) { /* ignore */ }
    },
  });

  const shouldShow = (name) => {
    if (name === "All Banks") return isAllBanks;
    const key = name.toLowerCase().replaceAll(" ", "_");
    return enabledMenuItems.includes(key) || isAllFeaturesUser;
  };

  const getVisibleChildren = (item) => {
    if (!item.children) return [];
    return item.children.filter(c =>
      enabledMenuItems.includes(c.name.toLowerCase().replaceAll(" ", "_"))
    );
  };

  return (
    <Box
      bgGradient="linear(to-b, #312e81, #000000)"
      color="white"
      w={{ base: "full", md: 60 }}
      pos="fixed"
      h="100vh"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      
      zIndex={10}
    >
      {/* Logo */}
      <Flex h="20" alignItems="center" justifyContent="space-between" px="6">
        <Image src={mainLogo} h="44px" filter="brightness(1.4)" />
        <CloseButton display={{ base: "flex", md: "none" }} onClick={onClose} color="white" />
      </Flex>

      {/* Menu */}
      <Box
        flex="1"
        overflowY="auto"
        px="3"
        pb="6"
        css={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(167, 139, 255, 0.4)",
            borderRadius: "full",
          },
        }}
      >
        {LinkItems.filter(item => shouldShow(item.name)).map((link) => (
          <NavItem
            key={link.name}
            itemName={link.name}
            icon={link.icon}
            to={link.to}
            onClick={link.onClick}
            childrens={getVisibleChildren(link)}
          />
        ))}
      </Box>

      {/* Bottom */}
      <Box
        borderTop="1px solid rgba(167, 139, 255, 0.3)"
        bg="rgba(0, 0, 0, 0.5)"
        backdropFilter="blur(12px)"
        p="4"
      >
        <SelectedOrganization />
      </Box>
    </Box>
  );
}